/**
 * Справочник товаров для выбора без связи (T-184): копия активных товаров в localStorage.
 * Обновляется при входе и восстановлении связи; без связи поиск идёт по этой копии.
 */

import { productsListProducts } from '@/api/generated/endpoints/products/products';
import type { ProductRef } from '@/api/generated/model';
import { isNetworkError } from '@/offline/network';

const KEY = 'sww.products.v1';
const PAGE = 200;
const MAX_PAGES = 50;

/** 'network' — сервер недоступен: повод показать «Нет связи» сразу при запуске. */
export async function refreshProductCache(): Promise<'ok' | 'network' | 'error'> {
  try {
    const all: ProductRef[] = [];
    for (let page = 0; page < MAX_PAGES; page += 1) {
      const res = await productsListProducts({
        status: 'active',
        limit: PAGE,
        offset: page * PAGE,
      });
      all.push(
        ...res.items.map((p) => ({
          id: p.id,
          name: p.name,
          base_uom: p.base_uom,
          is_active: p.is_active,
        })),
      );
      if (all.length >= res.total || res.items.length < PAGE) break;
    }
    window.localStorage.setItem(KEY, JSON.stringify(all));
    return 'ok';
  } catch (e) {
    // нет связи или хранилища — останется прежняя копия
    return isNetworkError(e) ? 'network' : 'error';
  }
}

export function cachedProducts(): ProductRef[] {
  try {
    const raw = window.localStorage.getItem(KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as ProductRef[]) : [];
  } catch {
    return [];
  }
}

/** Поиск без сервера: все слова запроса — в названии (регистр и «ё» не важны). */
export function searchCached(query: string, limit = 20): ProductRef[] {
  const norm = (s: string) => s.toLowerCase().replaceAll('ё', 'е');
  const words = norm(query).split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  return cachedProducts()
    .filter((p) => {
      const name = norm(p.name);
      return words.every((w) => name.includes(w));
    })
    .slice(0, limit);
}
